import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, Edit2, Trash2, X, Search, Loader2, AlertTriangle, AlertCircle, Info, HelpCircle, ChevronDown } from 'lucide-react';
import { reconRulesApi } from '../../services/api';
import DataTable from '../../components/DataTable';
import { mockRules } from './mockData';

const RULE_TYPES = [
  { value: 'row_count', label: 'Row Count' },
  { value: 'field_validation', label: 'Field Validation' },
  { value: 'cross_reference', label: 'Cross Reference' },
  { value: 'balance_check', label: 'Balance Check' },
  { value: 'completeness', label: 'Completeness' },
  { value: 'custom_sql', label: 'Custom SQL' },
];

const CATEGORIES = ['general', 'inbound', 'outbound'];
const SEVERITIES = ['error', 'warning', 'info'];

const SeverityBadge = ({ severity }) => {
  const styles = {
    error: 'bg-red-100 text-red-800',
    warning: 'bg-yellow-100 text-yellow-800',
    info: 'bg-blue-100 text-blue-800',
  };
  const icons = {
    error: <AlertCircle className="w-3 h-3" />,
    warning: <AlertTriangle className="w-3 h-3" />,
    info: <Info className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[severity] || styles.info}`}>
      {icons[severity]} {severity}
    </span>
  );
};

const HowTo = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-medium text-blue-800">
          <HelpCircle className="w-4 h-4" /> How to use Rules
        </span>
        <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-blue-900 space-y-3">
          <div>
            <p className="font-semibold mb-1">What are Rules?</p>
            <p>Rules are individual validation checks that verify file data integrity. Each rule contains a SQL template that runs against your Redshift data to check conditions like row counts, missing fields, or balance totals.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Steps</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li><strong>Create a Rule</strong> - Click "Add Rule" and fill in the code, name, type, and SQL template. Use <code className="bg-blue-100 px-1 rounded">{'{{placeholders}}'}</code> for configurable parameters.</li>
              <li><strong>Choose a Rule Type</strong>:
                <ul className="list-disc ml-4 mt-1 space-y-1 text-blue-800">
                  <li><strong>Row Count</strong> - Compare record counts between two tables. <em>E.g., "Eligibility file has 5,000 rows but staging table only has 4,998"</em></li>
                  <li><strong>Field Validation</strong> - Check for NULLs, duplicates, or invalid values. <em>E.g., "3 members have NULL email addresses" or "12 duplicate member_ids found"</em></li>
                  <li><strong>Cross Reference</strong> - Verify referential integrity between tables. <em>E.g., "15 consumer_codes in task_completion don't exist in ref_consumer"</em></li>
                  <li><strong>Balance Check</strong> - Verify amounts/totals match. <em>E.g., "Value load file total is $125,000 but FIS reports $124,850"</em></li>
                  <li><strong>Completeness</strong> - Ensure all expected records arrived. <em>E.g., "50 members from last month's eligibility file are missing this month"</em></li>
                  <li><strong>Custom SQL</strong> - Write any read-only SQL for custom checks. <em>E.g., "Check that all card orders have valid BIN ranges"</em></li>
                </ul>
              </li>
              <li><strong>Set Parameters</strong> - Define default parameters as JSON (e.g., schema, table, date_filter). These can be overridden when the rule is added to a ruleset.</li>
              <li><strong>Write the SQL Template</strong> - Use placeholders that match your parameter keys. The template must be read-only (SELECT only).</li>
              <li><strong>Set Severity</strong> - Error (hard fail), Warning (soft fail), or Info (informational).</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold mb-1">Built-in Templates</p>
            <p>8 pre-configured rule templates are available: Row Count Match, Required Fields, Amount Balance, Duplicate Check, Completeness, Cross Reference, File Freshness, and Value Range. Edit these or create your own.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ReconRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    rule_code: '', rule_name: '', description: '', rule_type: 'row_count',
    rule_category: 'general', parameters_json: '', sql_template: '', severity: 'error', is_active: true,
  });

  const loadData = useCallback(async () => {
    try {
      let data;
      try {
        const res = await reconRulesApi.listRules(filterType || undefined);
        data = res.data;
      } catch { data = []; }
      setRules(data && data.length > 0 ? data : mockRules);
    } catch (err) {
      console.error('Failed to load rules:', err.message);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = rules.filter(r =>
    !search ||
    r.rule_code?.toLowerCase().includes(search.toLowerCase()) ||
    r.rule_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.rule_type?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingRule(null);
    setForm({
      rule_code: '', rule_name: '', description: '', rule_type: 'row_count',
      rule_category: 'general', parameters_json: '', sql_template: '', severity: 'error', is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      rule_code: rule.rule_code,
      rule_name: rule.rule_name,
      description: rule.description || '',
      rule_type: rule.rule_type,
      rule_category: rule.rule_category || 'general',
      parameters_json: rule.parameters_json || '',
      sql_template: rule.sql_template || '',
      severity: rule.severity || 'error',
      is_active: rule.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingRule) {
        await reconRulesApi.updateRule(editingRule.rule_id, form);
      } else {
        await reconRulesApi.createRule(form);
      }
      setShowModal(false);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!confirm(`Deactivate rule "${rule.rule_name}"?`)) return;
    try {
      await reconRulesApi.deleteRule(rule.rule_id);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const columns = [
    { key: 'rule_code', label: 'Code' },
    { key: 'rule_name', label: 'Name' },
    { key: 'rule_type', label: 'Type', render: (val) => {
      const t = RULE_TYPES.find(rt => rt.value === val);
      return <span className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-xs font-medium text-gray-700">{t?.label || val}</span>;
    }},
    { key: 'rule_category', label: 'Category' },
    { key: 'severity', label: 'Severity', render: (val) => <SeverityBadge severity={val} /> },
    { key: 'ruleset_count', label: 'Used In', format: 'number' },
    { key: 'is_active', label: 'Active', render: (val) =>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
        {val ? 'Active' : 'Inactive'}
      </span>
    },
    { key: 'actions', label: 'Actions', sortable: false, render: (_, row) => (
      <div className="flex gap-2">
        <button onClick={() => openEdit(row)} className="text-amber-600 hover:text-amber-800"><Edit2 className="w-4 h-4" /></button>
        <button onClick={() => handleDelete(row)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
      </div>
    )},
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <BookOpen className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reconciliation Rules</h1>
            <p className="text-sm text-gray-500">{rules.length} rules configured</p>
          </div>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors">
          <Plus className="w-4 h-4" /> Add Rule
        </button>
      </div>

      <HowTo />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search rules..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent" />
        </div>
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setLoading(true); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
          <option value="">All Types</option>
          {RULE_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <DataTable data={filtered} columns={columns} loading={loading} emptyMessage="No rules found. Create your first rule to get started." />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">{editingRule ? 'Edit Rule' : 'Create Rule'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Code *</label>
                  <input type="text" value={form.rule_code} disabled={!!editingRule}
                    onChange={e => setForm({...form, rule_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 disabled:bg-gray-100" placeholder="e.g. row_count_match" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
                  <input type="text" value={form.rule_name}
                    onChange={e => setForm({...form, rule_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400" placeholder="e.g. Row Count Match" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400" rows={2} />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select value={form.rule_type} onChange={e => setForm({...form, rule_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
                    {RULE_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={form.rule_category} onChange={e => setForm({...form, rule_category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                  <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
                    {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parameters JSON</label>
                <textarea value={form.parameters_json} onChange={e => setForm({...form, parameters_json: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 font-mono text-sm" rows={4}
                  placeholder='{"schema":"etl_core","table":"ref_consumer","date_filter":"1=1"}' />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SQL Template</label>
                <textarea value={form.sql_template} onChange={e => setForm({...form, sql_template: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 font-mono text-sm" rows={5}
                  placeholder="SELECT COUNT(*) as actual_value FROM {{schema}}.{{table}} WHERE {{date_filter}}" />
              </div>

              {/* Resolved SQL Preview */}
              {form.sql_template && form.parameters_json && (() => {
                try {
                  const params = JSON.parse(form.parameters_json);
                  let resolved = form.sql_template;
                  // Multiple passes to resolve nested placeholders (e.g. source_count_sql contains {{schema}})
                  for (let pass = 0; pass < 5; pass++) {
                    let changed = false;
                    for (const [key, value] of Object.entries(params)) {
                      const before = resolved;
                      resolved = resolved.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(value));
                      if (resolved !== before) changed = true;
                    }
                    if (!changed) break;
                  }
                  const hasUnresolved = /\{\{\w+\}\}/.test(resolved);
                  return (
                    <div className={`rounded-lg border p-3 ${hasUnresolved ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Resolved SQL Preview</label>
                        {hasUnresolved && <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">Has unresolved placeholders</span>}
                        {!hasUnresolved && <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">All parameters resolved</span>}
                      </div>
                      <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-all">{resolved}</pre>
                    </div>
                  );
                } catch {
                  return null;
                }
              })()}

              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                  className="rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
                <label className="text-sm text-gray-700">Active</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.rule_code || !form.rule_name || !form.rule_type}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingRule ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
